import { z } from "zod";
import { paginatedSchema, paginationQuerySchema } from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import {
  dependencyRefSchema,
  dependencyRequestTypeSchema,
  taskParamsSchema,
  taskSchema,
} from "./task.schemas.js";

// ---------------------------------------------------------------------------
// Comments (one level of threading: a reply's parent is always a top-level comment)
// ---------------------------------------------------------------------------

export const MAX_COMMENT_LENGTH = 10_000;

/** A comment mentions a person with `<@userId>` in its body. */
export const commentSchema = z.object({
  id: idSchema,
  taskId: idSchema,
  parentCommentId: idSchema.nullable(),
  /** Null once the author left the workspace. */
  author: userSummarySchema.nullable(),
  /** Empty for deleted comments (their text is erased). */
  body: z.string(),
  /** Users mentioned by the body who can see the project. */
  mentionedUserIds: z.array(idSchema),
  editedAt: isoDateTimeSchema.nullable(),
  /** Deleted comments stay in the thread as a placeholder so replies keep their context. */
  deleted: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type Comment = z.infer<typeof commentSchema>;

export const commentThreadSchema = commentSchema.extend({
  /** Replies, oldest first. */
  replies: z.array(commentSchema),
});
export type CommentThread = z.infer<typeof commentThreadSchema>;

const commentBodySchema = z
  .string()
  .trim()
  .min(1, "Write a comment")
  .max(MAX_COMMENT_LENGTH, `Comment must be at most ${String(MAX_COMMENT_LENGTH)} characters`);

export const createCommentSchema = z.object({
  body: commentBodySchema,
  /** Reply to this top-level comment of the same task. */
  parentCommentId: idSchema.optional(),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;

export const updateCommentSchema = z.object({ body: commentBodySchema });
export type UpdateCommentInput = z.infer<typeof updateCommentSchema>;

export const commentParamsSchema = taskParamsSchema.extend({ commentId: idSchema });

/** Oldest first; `nextCursor` continues towards newer top-level comments. */
export const listCommentsQuerySchema = paginationQuerySchema;
export type ListCommentsQuery = z.infer<typeof listCommentsQuerySchema>;

export const commentsResponseSchema = paginatedSchema(commentThreadSchema);
export type CommentsResponse = z.infer<typeof commentsResponseSchema>;
export const commentResponseSchema = z.object({ comment: commentSchema });
export type CommentResponse = z.infer<typeof commentResponseSchema>;

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export const TASK_ACTIVITY_TYPES = [
  "CREATED",
  "UPDATED",
  "STATUS_CHANGED",
  "ASSIGNEES_CHANGED",
  "COMMENT_ADDED",
  "COMMENT_EDITED",
  "COMMENT_DELETED",
  "CHECKLIST_CHANGED",
  "TAG_ADDED",
  "TAG_REMOVED",
  "DEPENDENCY_ADDED",
  "DEPENDENCY_REMOVED",
] as const;
export type TaskActivityType = (typeof TASK_ACTIVITY_TYPES)[number];
export const taskActivityTypeSchema = z.enum(TASK_ACTIVITY_TYPES);

/** One field that changed: `from` and `to` are display strings (null: empty or not recorded). */
export const activityChangeSchema = z.object({
  field: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
});
export type ActivityChange = z.infer<typeof activityChangeSchema>;

export const taskActivitySchema = z.object({
  id: idSchema,
  type: taskActivityTypeSchema,
  /** Null once the actor left the workspace. */
  actor: userSummarySchema.nullable(),
  createdAt: isoDateTimeSchema,
  /** UPDATED and STATUS_CHANGED: what changed, old to new. */
  changes: z.array(activityChangeSchema),
  /** ASSIGNEES_CHANGED: who was added and removed. */
  added: z.array(userSummarySchema),
  removed: z.array(userSummarySchema),
  /** Tag name, checklist title, or the other task's key, depending on `type`. */
  subject: z.string().nullable(),
  /** COMMENT_*: the comment concerned. */
  commentId: idSchema.nullable(),
});
export type TaskActivity = z.infer<typeof taskActivitySchema>;

/** Newest first. */
export const listActivityQuerySchema = paginationQuerySchema;
export type ListActivityQuery = z.infer<typeof listActivityQuerySchema>;
export const activityResponseSchema = paginatedSchema(taskActivitySchema);
export type ActivityResponse = z.infer<typeof activityResponseSchema>;

// ---------------------------------------------------------------------------
// Checklists
// ---------------------------------------------------------------------------

export const MAX_CHECKLISTS_PER_TASK = 20;
export const MAX_CHECKLIST_ITEMS = 100;

export const checklistItemSchema = z.object({
  id: idSchema,
  checklistId: idSchema,
  title: z.string(),
  checked: z.boolean(),
  position: z.number(),
  assignee: userSummarySchema.nullable(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const checklistSchema = z.object({
  id: idSchema,
  taskId: idSchema,
  title: z.string(),
  position: z.number(),
  items: z.array(checklistItemSchema),
});
export type Checklist = z.infer<typeof checklistSchema>;

const checklistTitleSchema = z
  .string()
  .trim()
  .min(1, "Title is required")
  .max(200, "Title must be at most 200 characters");

export const createChecklistSchema = z.object({ title: checklistTitleSchema });
export type CreateChecklistInput = z.infer<typeof createChecklistSchema>;
export const updateChecklistSchema = z.object({ title: checklistTitleSchema });
export type UpdateChecklistInput = z.infer<typeof updateChecklistSchema>;

export const createChecklistItemSchema = z.object({
  title: checklistTitleSchema,
  assigneeId: idSchema.nullable().optional(),
});
export type CreateChecklistItemInput = z.infer<typeof createChecklistItemSchema>;

/** Toggling is `{ checked }`. */
export const updateChecklistItemSchema = z
  .object({
    title: checklistTitleSchema.optional(),
    checked: z.boolean().optional(),
    assigneeId: idSchema.nullable().optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  });
export type UpdateChecklistItemInput = z.infer<typeof updateChecklistItemSchema>;

/** The complete new order: every id of the task's checklists exactly once. */
export const reorderChecklistsSchema = z.object({
  checklistIds: z.array(idSchema).max(MAX_CHECKLISTS_PER_TASK),
});
export type ReorderChecklistsInput = z.infer<typeof reorderChecklistsSchema>;
/** The complete new order: every id of the checklist's items exactly once. */
export const reorderChecklistItemsSchema = z.object({
  itemIds: z.array(idSchema).max(MAX_CHECKLIST_ITEMS),
});
export type ReorderChecklistItemsInput = z.infer<typeof reorderChecklistItemsSchema>;

export const checklistParamsSchema = taskParamsSchema.extend({ checklistId: idSchema });
export const checklistItemParamsSchema = checklistParamsSchema.extend({ itemId: idSchema });

export const checklistsResponseSchema = z.object({ checklists: z.array(checklistSchema) });
export type ChecklistsResponse = z.infer<typeof checklistsResponseSchema>;
export const checklistResponseSchema = z.object({ checklist: checklistSchema });
export type ChecklistResponse = z.infer<typeof checklistResponseSchema>;

// ---------------------------------------------------------------------------
// Tags on a task
// ---------------------------------------------------------------------------

export const taskTagParamsSchema = taskParamsSchema.extend({ tagId: idSchema });

// ---------------------------------------------------------------------------
// Dependencies
// ---------------------------------------------------------------------------

export const createDependencySchema = z.object({
  /** The other task, in the same project. */
  taskId: idSchema,
  /** How this task relates to the other: `BLOCKED_BY` means the other task blocks this one. */
  type: dependencyRequestTypeSchema,
});
export type CreateDependencyInput = z.infer<typeof createDependencySchema>;

export const dependencyParamsSchema = taskParamsSchema.extend({ dependencyId: idSchema });

export const dependenciesResponseSchema = z.object({ dependencies: z.array(dependencyRefSchema) });
export type DependenciesResponse = z.infer<typeof dependenciesResponseSchema>;

/** Dependency and tag changes return the task with its refreshed refs and tags. */
export const taskChangeResponseSchema = z.object({ task: taskSchema });
export type TaskChangeResponse = z.infer<typeof taskChangeResponseSchema>;

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

export const watchersResponseSchema = z.object({
  watchers: z.array(userSummarySchema),
  /** Whether the caller is among them. */
  watching: z.boolean(),
});
export type WatchersResponse = z.infer<typeof watchersResponseSchema>;
